import React, { useContext, useState, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSwireApp } from 'react-hooks';
import i18n from 'localization';

import { AppContext } from 'lane-shared/contexts';
import { Channel } from 'lane-shared/types/ChannelType';
import { useSimpleTrack } from 'lane-shared/hooks/useSimpleTrack';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';

import { Button } from 'design-system-web';
import ButtonStrip from 'components/general/ButtonStrip';
import { HookFormInput } from 'components/reactHookForm';
import { H4, S } from 'components/typography';

import styles from './EmailForm.scss';
import { ANALYTIC_KEYS } from 'constants-analytics';
import { SWIRE_LANGUAGE_ENUM, SWIRE_LANGUAGE_SELECTION } from 'constants-whitelabel';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';

type FormData = {
  email: string;
};

type Props = {
  isLoading: boolean;
  email?: string;
  onSubmit: (email: string, language?: string) => void;
  invitedChannel?: Channel;
};

export function EmailForm({
  isLoading,
  email,
  onSubmit,
  invitedChannel, 
}: Props) {
  const { whitelabel } = useContext(AppContext);
  const { isSwire: isSwireDomain } = useSwireApp(whitelabel);

  // Initialize conditionally based on Swire domain
  const [selectedLanguage, setSelectedLanguage] = useState<SWIRE_LANGUAGE_ENUM | undefined>(
    isSwireDomain ? SWIRE_LANGUAGE_ENUM.EN_US : undefined
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      email,
    },
  });
  const simpleTrack = useSimpleTrack();
  const { t } = useTranslation();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();
  const handleLanguageChange = (language: SWIRE_LANGUAGE_ENUM) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
  };

  // Clear any stale locale from storage on initial render
  // This prevents abandoned auth flows from affecting new login attempts
  useEffect(() => {
    Storage.removeItem(StorageKeys.USER_LOCALE_AT_LOGIN).catch((error) => {
      console.error('Failed to clear stale locale from storage', error);
    });
  }, []);

  const TRANSLATION_KEYS = {
    title: isWorkplaceOnboardingEnabled
      ? 'shared.identifyUser.loginEmail.web.join'
      : 'shared.identifyUser.loginEmail.web.connectToWhitelabel',
    subtitle: isWorkplaceOnboardingEnabled
      ? 'web.login.loginEmail.subtitle.workplaceOnboarding'
      : 'web.login.loginEmail.subtitle',
    emailLabel: 'shared.identifyUser.loginEmail.emailLabel',
    button: 'shared.identifyUser.loginEmail.button',
    requiredFieldError: 'shared.identifyUser.loginEmail.requiredField.error',
    swireTitlePrefix: 'shared.swire.identifyUser.loginEmail.title.prefix',
    swireTaiKooPlaceTitle: 'shared.swire.identifyUser.loginEmail.taiKooPlaceTitle',
    swirePacificPlaceOfficeTitle: 'shared.swire.identifyUser.loginEmail.pacificPlaceOfficeTitle',
    swireAppNameSuffix: 'shared.swire.identifyUser.loginEmail.title.sufix',
    swireSubtitle: 'shared.swire.identifyUser.loginEmail.subtitle',
  };

  const handleFormSubmission = async (
    { email }: FormData,
    method: 'bySubmit' | 'byTouch'
  ) => {
    const trimmedEmail = email.trim();

    simpleTrack(ANALYTIC_KEYS.ANALYTIC_USER_ONBOARDING_IDENTIFY_CONTINUE, {
      method,
    });

    if(isSwireDomain && selectedLanguage) {
      await Storage.setItem(StorageKeys.USER_LOCALE_AT_LOGIN, selectedLanguage);
      onSubmit(trimmedEmail, selectedLanguage);
    } else {
      onSubmit(trimmedEmail);
    }
    
  };

  const getTitlePrefix = () => {
    if(isSwireDomain) {
      return t(TRANSLATION_KEYS.swireTitlePrefix);
    }

    return t(TRANSLATION_KEYS.title);
  };

  const getTitle = () => {
    return !isSwireDomain ? name : '';
  };

  const getSubtitle = () => {
    if(isSwireDomain) {
      return t(TRANSLATION_KEYS.swireSubtitle);
    }

    return t(TRANSLATION_KEYS.subtitle);
  };

  const emailFieldError =
    errors.email?.type === 'required'
      ? [t(TRANSLATION_KEYS.requiredFieldError)]
      : null;

  const name =
    isWorkplaceOnboardingEnabled && invitedChannel
      ? (invitedChannel.profile?.name ?? invitedChannel.name)
      : whitelabel.name;



  return (
    <div className={styles.emailForm}>
      <div className={styles.titleContainer}>
      <H4 className={styles.title}>
        {getTitlePrefix()}
      </H4>
      <H4 className={styles.title}>
        {getTitle()}
      </H4>
      </div>
      <S>{getSubtitle()}</S>
      <div className={styles.formContainer}>
        <form
          onSubmit={handleSubmit(data =>
            handleFormSubmission(data, 'bySubmit')
          )}
        >
          <HookFormInput
            isRequired
            className={styles.emailInputWrapper}
            control={control}
            type="email"
            name="email"
            disabled={Boolean(email)}
            label={TRANSLATION_KEYS.emailLabel}
            errorMessage={emailFieldError}
          />

          <Button
            className={styles.submitButton}
            loading={isLoading}
            fullWidth
            size="large"
            type="button"
            onClick={handleSubmit(data =>
              handleFormSubmission(data, 'byTouch')
            )}
          >
            {t(TRANSLATION_KEYS.button)}
          </Button>
        </form>
        {isSwireDomain && (
          <ButtonStrip
            className={styles.languageButtonStrip}
            buttons={SWIRE_LANGUAGE_SELECTION}
            selected={selectedLanguage}
            onClick={handleLanguageChange}
            doTranslate={false}
          />
        )}
      </div>
    </div>
  );
}
